// index.js
import "./i18n/config";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./app/App";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { cleanupStalePwaCaches } from "./serviceWorkerRegistration";
import { handleIncomingChatPush } from "./utils/chatAlerts";
import { notifyCustomerBulkProposal } from "./utils/bulkProposalNotice";
import { notifyCustomerTimeProposal } from "./utils/acceptanceTimeProposal";



const root = ReactDOM.createRoot(document.getElementById("root"));

const rawGoogleClientId = String(process.env.REACT_APP_GOOGLE_CLIENT_ID ?? "").trim();
// CRA inlines env at compile time; hosts sometimes inject the literal "undefined".
const googleClientId =
  rawGoogleClientId && rawGoogleClientId !== "undefined" && rawGoogleClientId !== "null"
    ? rawGoogleClientId
    : "";
const appTree = <App />;

// Do not mount GoogleOAuthProvider with an empty clientId: @react-oauth/google can leave the
// tree broken in CI/E2E (no REACT_APP_GOOGLE_CLIENT_ID), so routes like /login never render #email.
root.render(
  <React.StrictMode>
    {googleClientId ? (
      <GoogleOAuthProvider clientId={googleClientId}>{appTree}</GoogleOAuthProvider>
    ) : (
      appTree
    )}
  </React.StrictMode>
);

// Drop leftover Workbox/CRA SWs + caches (deploy freshness), then keep FCM SW only.
if ("serviceWorker" in navigator) {
  cleanupStalePwaCaches()
    .then(() =>
      navigator.serviceWorker.register("/firebase-messaging-sw.js", { updateViaCache: "none" }),
    )
    .catch(() => {});

  // Listen for messages from SW
  navigator.serviceWorker.addEventListener("message", (event) => {
    // Device info request
    if (event.data?.type === "REQUEST_DEVICE_INFO") {
      const deviceInfo = {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        isPWA: window.matchMedia("(display-mode: standalone)").matches,
      };
      if (event.ports && event.ports[0]) {
        event.ports[0].postMessage(deviceInfo);
      }

    }

    // Optional: handle notification clicks from SW in foreground
    if (event.data?.type === "FOREGROUND_NOTIFICATION") {

    }

    // Direct print trigger from SW (notification tap) — avoids relying on FCM onMessage alone.
    if (event.data?.type === "PUSH_PRINT_ORDER") {
      const oid = event.data.payload?.orderId;
      if (oid) {
        import("./app/FCMTokenHandler").then(({ runOwnerThermalAutoPrint }) => {
          runOwnerThermalAutoPrint(String(oid));
        });
      }
      return;
    }

    // Inbox + badge replace the old in-app toast. Still refresh orders / print from the push.
    if (event.data?.type === "PUSH_NOTIFICATION_RECEIVED") {
      const payload = event.data.payload || {};
      handleIncomingChatPush(payload);
      if (String(payload.type || "") === "preorder_reminder") {
        window.dispatchEvent(new CustomEvent("inbox-notification-changed"));
        return;
      }
      const { orderId, url } = payload;
      const isOwnerNotif =
        String(payload.type || "") === "print" ||
        String(payload.type || "") === "dining_print" ||
        String(url || "").includes("/restaurant/");
      if (String(payload.type || "") === "bulk_slot_proposed") {
        notifyCustomerBulkProposal(payload);
      }
      if (String(payload.type || "") === "acceptance_time_proposed") {
        notifyCustomerTimeProposal(payload);
      }
      if ((orderId || payload.diningItemId) && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("owner-new-order", { detail: payload }));
        if (String(payload.type || "") === "dining_print") {
          window.dispatchEvent(new CustomEvent("owner-dining-kitchen-order", { detail: payload }));
        }
        if (!isOwnerNotif) {
          window.dispatchEvent(new CustomEvent("order-notification-received", { detail: payload }));
        }
      }
      // Background push: SW delivers here while WebView is alive → thermal print on Android.
      if (String(payload.type || "") === "print" && orderId) {
        import("./app/FCMTokenHandler").then(({ runOwnerThermalAutoPrint }) => {
          runOwnerThermalAutoPrint(String(orderId));
        });
      }
      if (
        String(payload.type || "") === "dining_print" &&
        payload.sessionId &&
        payload.diningItemId
      ) {
        import("./app/FCMTokenHandler").then(({ runOwnerThermalAutoPrintDining }) => {
          runOwnerThermalAutoPrintDining(String(payload.sessionId), String(payload.diningItemId));
        });
      }
      window.dispatchEvent(new CustomEvent("inbox-notification-changed"));
    }

    if (event.data?.type === "DRIVE_APPROACH_TAP") {
      const payload = event.data.payload || {};
      if (payload.orderId) {
        window.dispatchEvent(new CustomEvent("drive-approach-tap", { detail: payload }));
      }
    }

    // In-app route change. Full document navigation from the service worker reloads the SPA.
    if (event.data?.type === "NOTIFICATION_OPEN") {
      const url = String(event.data.url || "").trim();
      if (url.startsWith("/")) {
        window.__pendingNotificationPath = url;
        window.dispatchEvent(new CustomEvent("notification-open", { detail: { url } }));
      }
    }

    // Notification tap while app is already open — refresh order page without requiring reload
    if (event.data?.type === "ORDER_NOTIFICATION_CLICK") {
      const payload = event.data.payload || {};
      if (payload.orderId) {
        window.dispatchEvent(new CustomEvent("order-notification-received", { detail: payload }));
      }
    }
  });
}

// PWA caching SW disabled; FCM uses firebase-messaging-sw.js only.
// Stale Workbox/CRA workers are removed in cleanupStalePwaCaches().
